"use client";

import React, { useState, useEffect } from "react";
import { Building2, ArrowLeft } from "lucide-react";
import { getCompanyName } from "../lib/textron-data-processor";
import PayrollLanding from "./payroll/PayrollLanding";
import LibrariesPage from "./libraries/LibrariesPage";
import {
  mainContentRoutes,
  specialRoutes,
  renderSpecialRoute,
} from "./main-content-routes";
import DynamicWorkspaceLanding from "./DynamicWorkspaceLanding";
import DynamicWorkspaceSubstantive from "./DynamicWorkspaceSubstantive";
import PayrollRomms from "./payroll/PayrollRomms";
import WorkspaceInternalControls from "./libraries/WorkspaceInternalControls";
import WorkspaceDeficiencies from "./payroll/WorkspaceDeficiencies";
import WorkspaceMisstatements from "./payroll/WorkspaceMisstatements";
import CustomProcedureRoute from "./substantive-procedures/CustomProcedureRoute";
import Diagnostics from "./utility/Diagnostics";
import { Button } from "./ui/button";

interface MainContentProps {
  activeSection: string;
  searchTerm: string;
  setActiveSection?: (section: string) => void;
  onBack?: () => void;
}

export function MainContent({
  activeSection,
  searchTerm,
  setActiveSection,
  onBack,
}: MainContentProps) {
  const [companyName, setCompanyName] = useState<string>("ABC Private Limited");
  const [isLoadingCompanyName, setIsLoadingCompanyName] = useState<boolean>(false);

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      setIsLoadingCompanyName(true);
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name from SharePoint, using fallback');
        setCompanyName("ABC Private Limited");
      } finally {
        setIsLoadingCompanyName(false);
      }
    };
    loadCompanyName();
  }, []);

  const renderContent = () => {
    // Handle dynamic workspaces (user-added) with encoded info in the activeSection key
    if (activeSection.startsWith("dynamic-workspace:")) {
      const [, area, prefix, encodedName] = activeSection.split(":");
      const workspaceName = decodeURIComponent(encodedName || "");
      const workspaceId = prefix || "";
      const areaKey =
        area === "Execution" || area === "Planning" || area === "ConcludingProcedures"
          ? (area as "Planning" | "Execution" | "ConcludingProcedures")
          : "Execution";

      return (
        <DynamicWorkspaceLanding
          area={areaKey}
          workspaceName={workspaceName}
          workspaceId={workspaceId}
          onSelect={setActiveSection || (() => { })}
        />
      );
    }

    // Dynamic workspace RoMMs view
    if (activeSection.startsWith("dynamic-workspace-romms:")) {
      const [, area, prefix, encodedName] = activeSection.split(":");
      const workspaceName = decodeURIComponent(encodedName || "");
      const landingKey = `dynamic-workspace:${area}:${prefix}:${encodedName}`;
      return (
        <PayrollRomms
          workspace={workspaceName}
          onBack={() => setActiveSection?.(landingKey)}
          onComplete={() => setActiveSection?.(landingKey)}
        />
      );
    }

    // Dynamic workspace Internal Controls – driven by Libraries_RCM.json
    if (activeSection.startsWith("dynamic-workspace-controls:")) {
      const [, area, prefix, encodedName] = activeSection.split(":");
      const workspaceName = decodeURIComponent(encodedName || "");
      const landingKey = `dynamic-workspace:${area}:${prefix}:${encodedName}`;
      
      // Ensure setActiveSection is available
      if (!setActiveSection) {
        console.error('[MainContent] ERROR: setActiveSection is UNDEFINED when rendering WorkspaceInternalControls');
        return <div style={{color: 'white', padding: '20px'}}>Error: Navigation function not available. Please refresh the page.</div>;
      }
      
      return (
        <WorkspaceInternalControls
          workspaceName={workspaceName}
          setActiveSection={setActiveSection}
          controlsListSection={activeSection}
        />
      );
    }

    // Dynamic workspace Deficiencies
    if (activeSection.startsWith("dynamic-workspace-deficiencies:")) {
      const [, area, prefix, encodedName] = activeSection.split(":");
      const workspaceName = decodeURIComponent(encodedName || "");
      const landingKey = `dynamic-workspace:${area}:${prefix}:${encodedName}`;
      return (
        <WorkspaceDeficiencies
          workspaceName={workspaceName}
        />
      );
    }

    // Dynamic workspace Misstatements
    if (activeSection.startsWith("dynamic-workspace-misstatements:")) {
      const [, area, prefix, encodedName] = activeSection.split(":");
      const workspaceName = decodeURIComponent(encodedName || "");
      const landingKey = `dynamic-workspace:${area}:${prefix}:${encodedName}`;
      return (
        <WorkspaceMisstatements
          workspaceName={workspaceName}
        />
      );
    }

    // Dynamic workspace Substantive Procedures
    if (activeSection.startsWith("dynamic-workspace-substantive:")) {
      const [, area, prefix, encodedName] = activeSection.split(":");
      const workspaceName = decodeURIComponent(encodedName || "");
      const workspaceId = prefix || "";
      const areaKey =
        area === "Planning" || area === "Execution" || area === "ConcludingProcedures"
          ? (area as "Planning" | "Execution" | "ConcludingProcedures")
          : "Execution";
      const landingKey = `dynamic-workspace:${area}:${prefix}:${encodedName}`;
      return (
        <DynamicWorkspaceSubstantive
          area={areaKey}
          workspaceName={workspaceName}
          workspaceId={workspaceId}
          onBack={() => setActiveSection?.(landingKey)}
          setActiveSection={setActiveSection}
        />
      );
    }

    // Internal Control routes - format: internal-control:<controlId>:<section>
    if (activeSection.startsWith("internal-control:")) {
      return (
        <LibrariesPage
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
      );
    }

    // Custom Procedure routes - format: custom-procedure:<workspace>:<procedure-name>
    if (activeSection.startsWith("custom-procedure:")) {
      // Parse workspace name from route to determine back route
      const parts = activeSection.split(":");
      let backRoute = "";
      if (parts.length >= 2) {
        try {
          const workspaceName = decodeURIComponent(parts[1]);
          // Determine back route based on workspace
          if (workspaceName === "Employee Benefits Expense") {
            backRoute = "payroll-substantive";
          } else {
            // For other workspaces, try to get prefix and construct route
            // This will be handled by CustomProcedureRoute component
            backRoute = ""; // Will be determined dynamically
          }
        } catch (e) {
          console.warn("Failed to parse workspace name from route", e);
        }
      }

      return (
        <CustomProcedureRoute
          route={activeSection}
          onBack={() => {
            if (backRoute && setActiveSection) {
              setActiveSection(backRoute);
            } else if (onBack) {
              onBack();
            }
          }}
          setActiveSection={setActiveSection}
        />
      );
    }

    // Handle special routes with custom logic
    if (activeSection in specialRoutes) {
      const specialContent = renderSpecialRoute(
        activeSection,
        searchTerm,
        setActiveSection,
      );
      if (specialContent) return specialContent;
    }

    // Handle regular routes using configuration (library routes may include ":workspace" suffix for filter)
    let routeKey = activeSection;
    if (activeSection?.startsWith("confirmation-library:")) {
      routeKey = "confirmation-library";
    } else if (activeSection?.startsWith("sampling-library:")) {
      routeKey = "sampling-library";
    }
    const routeConfig = mainContentRoutes[routeKey];
    if (routeConfig) {
      const Component = routeConfig.component;
      const props = { ...routeConfig.props };

      // Replace searchTerm placeholder with actual value
      if (props.searchTerm === true) {
        props.searchTerm = searchTerm;
      }

      // Pass activeSection and setActiveSection to LibrariesPage
      if (Component === LibrariesPage) {
        props.activeSection = activeSection;
        if (setActiveSection) {
          props.setActiveSection = setActiveSection;
        }
      }

      // Handle components that need setActiveSection
      if (routeConfig.requiresSetActiveSection && setActiveSection) {
        // Use the actual component from the route config, not hardcoded PayrollLanding
        // Merge route props with onSelect
        const componentProps = { ...props, onSelect: setActiveSection };
        return <Component {...componentProps} />;
      }

      // Pass setActiveSection to all components that might need it
      const componentProps = { ...props };
      if (setActiveSection) {
        componentProps.setActiveSection = setActiveSection;
        // Also pass as onSelect for components that use that prop name
        componentProps.onSelect = setActiveSection;
      }

      return <Component {...componentProps} />;
    }

    // Default fallback
    return (
      <div className="flex h-full items-center justify-center">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-indigo-100">
            <Building2 className="h-12 w-12 text-blue-600" />
          </div>
          <h2 className="mb-3 text-2xl font-bold text-gray-900">
            Select a Report
          </h2>
          <p className="leading-relaxed text-gray-600">
            Choose a financial report from the left panel to view detailed
            analysis and insights for {companyName}.
          </p>
        </div>
      </div>
    );
  };

  // Don't show back button on initial/default screens
  const hideBackButton = activeSection === "chat" ||
    activeSection === "engagement-overview" ||
    activeSection === "overview" ||
    activeSection === "libraries" ||
    activeSection === "partner-review-dashboard" ||
    activeSection === "partner-review-planning" ||
    activeSection === "partner-review-concluding" ||
    activeSection === "detailed-planning-review-dashboard" ||
    activeSection === "eqr-dashboard" ||
    activeSection === "eqr-checklist" ||
    activeSection === "planning-overview" ||
    activeSection === "diagnostics" ||
    activeSection === "archival" ||
    activeSection === "settings" ||
    activeSection === "bin" ||
    activeSection === "recycle-bin";

  return (
    <div className="flex-1 overflow-auto bg-background pb-8">
      {!hideBackButton && onBack ? (
        <div className="sticky top-0 z-10 border-b border-white/10 bg-black/40 backdrop-blur-sm px-8 pt-4 pb-2">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-white/80 hover:text-white hover:bg-white/10"
              data-allow-archival-edit={true}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      ) : null}
      <div className="p-8">{renderContent()}</div>
    </div>
  );
}
